import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { fmt } from '@dcc-erp/shared';
import type { PrepaidReconException, PrepaidRollforwardRow } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { usePrepaidReconExceptions, usePrepaidRollforward } from '../../lib/queries';
import { exportRows, todayStamp, type ExportColumn } from '../../lib/xlsx';
import { DimensionFilter } from '../dimensions';
import { PREPAID_ASSET_CODE } from './pp-helpers';

// ============================================================
// P8 — Roll-forward & GL reconciliation tab (กระทบยอด & Roll-forward).
// Per-contract Opening+Additions−Amortization−Write-off=Closing for a period;
// TOTAL closing diffed vs the live 10190001 GL balance → a variance light. Behind
// a red light: the reconciliation exception worklist (lazy-loaded).
// ============================================================

function defaultPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
}
function periodToInput(period: string): string {
  return `${period.slice(0, 4)}-${period.slice(4, 6)}`;
}
function inputToPeriod(value: string): string {
  return value.replace('-', '');
}

const RECON_CATEGORY_LABEL: Record<PrepaidReconException['category'], string> = {
  UNAMORTIZED_ACTIVE: 'ยังไม่ตัดจ่าย',
  NULL_JV_ENTRY: 'ไม่มีเลข JV',
  BALANCE_MISMATCH: 'ยอดไม่ตรง',
  STRANDED_RESIDUAL: 'ยอดค้างหลังสิ้นสุด',
  ROGUE_GL_LINE: 'รายการ manual ใน 10190001',
};

const ROLLFORWARD_EXPORT_COLS: ExportColumn<PrepaidRollforwardRow>[] = [
  { header: 'เลขที่', value: (r) => r.code, width: 18 },
  { header: 'รายละเอียด', value: (r) => r.description, width: 30 },
  { header: 'ผู้ขาย', value: (r) => r.vendorName ?? '', width: 20 },
  { header: 'สาขา', value: (r) => r.branchName ?? '', width: 16 },
  { header: 'ยอดยกมา', value: (r) => Number(r.opening), width: 16 },
  { header: 'เพิ่มขึ้น', value: (r) => Number(r.additions), width: 14 },
  { header: 'ตัดจ่าย', value: (r) => Number(r.amortization), width: 14 },
  { header: 'ตัดจำหน่าย', value: (r) => Number(r.writeOff), width: 14 },
  { header: 'ยอดคงเหลือ', value: (r) => Number(r.closing), width: 16 },
];

export function PPRollforward({ companyId }: { companyId: string | undefined }) {
  const [period, setPeriod] = useState<string>(defaultPeriod());
  const [branchId, setBranchId] = useState<string>('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [showWorklist, setShowWorklist] = useState<boolean>(false);

  const filters = {
    ...(branchId ? { branchId } : {}),
    ...(departmentId ? { departmentId } : {}),
  };
  const { data, isPending } = usePrepaidRollforward(companyId, period, filters);
  // Only judge balance once data is present — never paint a reassuring green while
  // the query is loading/blank (variance would default to 0 = a false "reconciled").
  const ready = !!data && !isPending;
  const variance = data ? Number(data.variance) : 0;
  const balanced = ready && Math.abs(variance) < 0.005;
  const unbalanced = ready && !balanced;
  const { data: worklist } = usePrepaidReconExceptions(companyId, period, showWorklist);

  async function onExport() {
    if (!data) return;
    await exportRows(
      `prepaid-rollforward-${period}-${todayStamp()}.xlsx`,
      'Roll-forward',
      ROLLFORWARD_EXPORT_COLS,
      data.rows,
    );
  }

  return (
    <div>
      {/* Controls */}
      <div className="filter-row" style={{ marginBottom: 'var(--section-gap)' }}>
        <input
          className="input w-sm"
          type="month"
          aria-label="งวด"
          value={periodToInput(period)}
          onChange={(e) => {
            setPeriod(inputToPeriod(e.target.value));
            setShowWorklist(false);
          }}
        />
        <DimensionFilter
          companyId={companyId}
          branchId={branchId}
          departmentId={departmentId}
          onBranchChange={setBranchId}
          onDepartmentChange={setDepartmentId}
        />
        <div style={{ flex: 1 }} />
        <button className="btn sm" onClick={() => void onExport()} disabled={!data}>
          <Icon name="download" size={12} /> ส่งออก Excel
        </button>
      </div>

      {/* Variance light (mirrors the go-live tie-out panel) */}
      <div
        className="card"
        style={{
          marginBottom: 'var(--section-gap)',
          padding: '14px 18px',
          border: `1px solid ${unbalanced ? 'var(--negative)' : balanced ? 'var(--positive)' : 'var(--border)'}`,
          background: unbalanced
            ? 'var(--negative-bg, #fef2f2)'
            : balanced
              ? 'var(--positive-bg, #f0fdf4)'
              : 'var(--surface, #fff)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div className="meta">ยอดคงเหลือตามทะเบียน (ปิดงวด)</div>
            <div className="tnum" style={{ fontSize: 20, fontWeight: 600 }}>
              {fmt(Number(data?.totals.closing ?? 0))}
            </div>
          </div>
          <div>
            <div className="meta">ยอดคงเหลือใน GL (10190001)</div>
            <div className="tnum" style={{ fontSize: 20, fontWeight: 600 }}>
              {fmt(Number(data?.glBalance ?? 0))}
            </div>
          </div>
          <div>
            <div className="meta">ส่วนต่าง (Variance)</div>
            <div
              className="tnum"
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: unbalanced
                  ? 'var(--negative)'
                  : balanced
                    ? 'var(--positive)'
                    : 'var(--text-muted)',
              }}
            >
              {ready ? fmt(variance) : '—'}
            </div>
          </div>
          <div style={{ flex: 1 }} />
          {!ready ? (
            <span className="meta">กำลังคำนวณ…</span>
          ) : balanced ? (
            <span className="badge positive">
              <span className="bdot" /> กระทบยอดแล้ว
            </span>
          ) : (
            <button
              className="btn sm"
              style={{
                color: 'var(--negative)',
                borderColor: 'var(--negative)',
              }}
              onClick={() => setShowWorklist((v) => !v)}
            >
              {showWorklist ? 'ซ่อนรายการที่ต้องตรวจ' : 'ดูรายการที่ต้องตรวจ'}
            </button>
          )}
        </div>
      </div>

      {/* Exception worklist (lazy, behind a red light) */}
      {showWorklist && unbalanced && (
        <div className="card" style={{ marginBottom: 'var(--section-gap)', padding: '12px 16px' }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>รายการที่ต้องตรวจสอบ</div>
          {!worklist ? (
            <div className="meta">กำลังโหลด…</div>
          ) : worklist.exceptions.length === 0 ? (
            <div className="meta">ไม่พบรายการผิดปกติในระดับ sub-ledger (ส่วนต่างอาจมาจาก GL)</div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>ประเภท</th>
                  <th>สัญญา</th>
                  <th>รายละเอียด</th>
                  <th className="num">จำนวน</th>
                  <th>ลิงก์</th>
                </tr>
              </thead>
              <tbody>
                {worklist.exceptions.map((ex, i) => (
                  <tr key={i}>
                    <td>
                      <span className="badge warning">{RECON_CATEGORY_LABEL[ex.category]}</span>
                    </td>
                    <td className="code">{ex.contractCode ?? '—'}</td>
                    <td>{ex.detail}</td>
                    <td className="num tnum">{ex.amount != null ? fmt(Number(ex.amount)) : '—'}</td>
                    <td>
                      {ex.journalEntryId ? (
                        <Link
                          to="/journal"
                          search={{ jv: ex.journalEntryId }}
                          style={{ color: 'var(--brand)' }}
                        >
                          ดู JV
                        </Link>
                      ) : (
                        <Link
                          to="/ledger"
                          search={{ accountCode: PREPAID_ASSET_CODE }}
                          style={{ color: 'var(--brand)' }}
                        >
                          ดู GL
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Roll-forward table */}
      <div className="tbl-wrap" style={{ maxHeight: 540 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>เลขที่</th>
              <th>ผู้ขาย / รายละเอียด</th>
              <th className="num">ยอดยกมา</th>
              <th className="num">เพิ่มขึ้น</th>
              <th className="num">ตัดจ่าย</th>
              <th className="num">ตัดจำหน่าย</th>
              <th className="num">ยอดคงเหลือ</th>
            </tr>
          </thead>
          <tbody>
            {isPending ? (
              <tr>
                <td colSpan={7} className="meta" style={{ textAlign: 'center', padding: 20 }}>
                  กำลังโหลด…
                </td>
              </tr>
            ) : !data || data.rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="meta" style={{ textAlign: 'center', padding: 20 }}>
                  ไม่มีการเคลื่อนไหวในงวดนี้
                </td>
              </tr>
            ) : (
              data.rows.map((r) => (
                <tr key={r.contractId}>
                  <td className="code">{r.code}</td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{r.vendorName ?? '—'}</div>
                    <div className="meta">{r.description}</div>
                  </td>
                  <td className="num tnum">{fmt(Number(r.opening))}</td>
                  <td className="num tnum">{fmt(Number(r.additions))}</td>
                  <td className="num tnum">{fmt(Number(r.amortization))}</td>
                  <td className="num tnum">{fmt(Number(r.writeOff))}</td>
                  <td className="num tnum">
                    <b>{fmt(Number(r.closing))}</b>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {data && data.rows.length > 0 && (
            <tfoot>
              <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                <td colSpan={2}>รวม</td>
                <td className="num tnum">{fmt(Number(data.totals.opening))}</td>
                <td className="num tnum">{fmt(Number(data.totals.additions))}</td>
                <td className="num tnum">{fmt(Number(data.totals.amortization))}</td>
                <td className="num tnum">{fmt(Number(data.totals.writeOff))}</td>
                <td className="num tnum">{fmt(Number(data.totals.closing))}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
